import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { CollaboratorRoleEnum } from "@models/collaborator";
import { Coupon } from "@models/coupon";
import { CouponsService } from "@services/coupons.service";
import { EventsManagementService } from "@services/events-management.service";
import { SnackBarService } from "@services/snack-bar.service";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import { sortBatchesByStartDate } from "../../../utils/utils";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { PieChartComponent } from "../../shared/components/pie-chart/pie-chart.component";
import { ConfirmActionModalComponent } from "../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import { CouponFormModalComponent } from "../producer-coupons-page/coupon-form-modal/coupon-form-modal.component";

@Component({
  selector: "app-coupon-detail-page",
  imports: [
    CommonModule,
    AppLayoutComponent,
    PageHeaderComponent,
    ButtonComponent,
    PieChartComponent,
    TableModule,
    PaginatorModule,
    SkeletonModule,
  ],
  templateUrl: "./coupon-detail-page.component.html",
  styleUrl: "./coupon-detail-page.component.scss",
})
export class CouponDetailPageComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _couponsService = inject(CouponsService);
  private readonly _eventsService = inject(EventsManagementService);
  private readonly _snackbar = inject(SnackBarService);
  private readonly _dialog = inject(MatDialog);

  private readonly _refresh$ = new BehaviorSubject<void>(undefined);
  private readonly _redemptionsPage$ = new BehaviorSubject<number>(1);

  readonly downloading = signal(false);
  first = 0;
  rows = 20;

  private get _couponId(): number {
    return Number(this._route.snapshot.paramMap.get("id"));
  }

  private get _eventId(): number {
    return Number(this._route.snapshot.queryParamMap.get("event_id"));
  }

  readOnly$ = of(this._eventId).pipe(
    switchMap((eventId) =>
      eventId
        ? this._eventsService.getMyEventById(eventId).pipe(
            map((event) => event.role === CollaboratorRoleEnum.OBSERVER),
            catchError(() => of(false)),
          )
        : of(false),
    ),
  );

  coupon$ = this._refresh$.pipe(
    switchMap(() => this._couponsService.get(this._eventId, this._couponId)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  segments$ = this.coupon$.pipe(
    map((coupon) => [
      { label: "Utilizados", value: coupon.chart.used_percentage },
      { label: "Não Utilizados", value: coupon.chart.unused_percentage },
    ]),
  );

  // Categoria(s)-alvo do cupom: o backend só devolve ticket_ids, então
  // buscamos os lotes do evento e resolvemos cada id em "Título — Lote N"
  // (mesma numeração por data de início usada no form).
  ticketCategories$ = this.coupon$.pipe(
    switchMap((coupon) =>
      this._eventsService.getEventBatches(coupon.event_id).pipe(
        map((batches) => {
          const labelById = new Map<number, string>();
          sortBatchesByStartDate(batches).forEach((batch, index) => {
            batch.tickets.forEach((ticket) => {
              if (ticket.id != null) {
                labelById.set(ticket.id, `${ticket.title} — Lote ${index + 1}`);
              }
            });
          });
          return coupon.ticket_ids.map(
            (id) => labelById.get(id) ?? `Ingresso ${id}`,
          );
        }),
        catchError(() => of<string[]>([])),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  redemptions$ = this._redemptionsPage$.pipe(
    switchMap((page) =>
      this._couponsService.redemptions(this._eventId, this._couponId, page),
    ),
  );

  goBack() {
    this._router.navigate(["/producer-area/event", this._eventId], {
      queryParams: { tab: 6 },
    });
  }

  handleRedemptionsPage(event: PaginatorState) {
    this._redemptionsPage$.next((event.page ?? 0) + 1);
  }

  editCoupon(coupon: Coupon) {
    this._dialog
      .open(CouponFormModalComponent, {
        width: "600px",
        autoFocus: false,
        data: { eventId: coupon.event_id, coupon },
      })
      .afterClosed()
      .pipe(
        filter((saved): saved is Coupon => !!saved),
        tap(() => this._refresh$.next()),
      )
      .subscribe();
  }

  closeCoupon(coupon: Coupon) {
    this._dialog
      .open(ConfirmActionModalComponent, {
        width: "500px",
        data: {
          modalTitle: "Encerrar cupom?",
          modalContent: `Deseja encerrar o cupom ${coupon.code}? O histórico de quem já usou será mantido e o cupom não poderá ser reaberto.`,
          confirmMessage: "Encerrar",
          cancelMessage: "Cancelar",
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this._couponsService.close(coupon.event_id, coupon.id).pipe(
            tap(() => {
              this._snackbar.openSnackBar(
                "Cupom encerrado com sucesso.",
                "Fechar",
                "success",
              );
              this._refresh$.next();
            }),
            catchError((err) => {
              this._snackbar.openSnackBar(
                err?.error?.error ?? "Não foi possível encerrar o cupom.",
                "Fechar",
                "error",
              );
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  downloadReport(coupon: Coupon) {
    this.downloading.set(true);
    this._couponsService
      .downloadUsageReport(coupon.event_id, coupon.id)
      .pipe(
        catchError(() => {
          this._snackbar.openSnackBar(
            "Não foi possível gerar o relatório.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => this.downloading.set(false)),
      )
      .subscribe((blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `relatorio_cupom_${coupon.code}.pdf`;
        anchor.click();
        URL.revokeObjectURL(url);
      });
  }
}
